// Shared markdown styles for consistent rendering across chat components
export const getMarkdownStyles = (theme) => ({
  // Base font family for all markdown content
  fontFamily: "Mulish, sans-serif",

  // Remove default margins/padding from container
  margin: 0,
  padding: 0,

  // Paragraph styles
  "& p": {
    margin: "2px 0",
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
    fontFamily: "Mulish, sans-serif",
    fontSize: "16px",
    "&:first-of-type": {
      marginTop: 0,
    },
    "&:last-of-type": {
      marginBottom: 0,
    },
    "&:last-child": {
      marginBottom: "0 !important",
    },
  },

  // Add extra spacing between paragraphs, but not within lists
  "& p + p": {
    marginTop: "16px !important",
  },

  // Reset spacing for paragraphs inside lists
  "& ul p, & ol p, & li p": {
    marginTop: "2px !important",
    marginBottom: "2px !important",
  },

  // Override inline styles for paragraphs not in lists
  "& p:not(ul p):not(ol p):not(li p)": {
    margin: "16px 0 2px 0 !important",
  },

  // Code block styles
  "& pre": {
    padding: "8px",
    borderRadius: "4px",
    fontSize: "14px",
    fontFamily: "Mulish, sans-serif",
    overflow: "auto",
    margin: "8px 0",
  },

  // Inline code styles
  "& code": {
    backgroundColor: theme.palette.grey.A100,
    padding: "2px 4px",
    borderRadius: "3px",
    fontSize: "14px",
    fontFamily: "Mulish, sans-serif",
  },

  // Table styles
  "& table": {
    display: "block",
    width: "100%",
    overflowX: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "collapse",
    fontSize: "14px",
    fontFamily: "Mulish, sans-serif",
    margin: "8px 0",
  },

  "& table th, & table td": {
    padding: "6px 8px",
    textAlign: "left",
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
    minWidth: "80px",
    color: "#333",
    backgroundColor: "white",
    fontFamily: "Mulish, sans-serif",
  },

  "& table th": {
    backgroundColor: theme.palette.grey.A100,
    fontWeight: 600,
    fontFamily: "Mulish, sans-serif",
  },

  "& table tr:nth-of-type(even)": {
    backgroundColor: "#f9f9f9",
  },

  // List styles
  "& ul": {
    margin: "8px 0",
    paddingLeft: "20px",
    listStyleType: "disc",
    fontFamily: "Mulish, sans-serif",
    fontSize: "16px",
  },

  "& ol": {
    margin: "8px 0",
    paddingLeft: "20px",
    listStyleType: "decimal",
    fontFamily: "Mulish, sans-serif",
    fontSize: "16px",
  },

  "& li": {
    margin: "4px 0",
    lineHeight: 1.4,
  },

  "& li > span:first-of-type": {
    marginTop: "-2px",
  },

  "& ul ul": {
    listStyleType: "circle",
    margin: "0",
  },

  "& ul ul ul": {
    listStyleType: "square",
  },

  // Header styles
  "& h1, & h2, & h3, & h4, & h5, & h6": {
    margin: "16px 0 8px 0",
    fontWeight: 600,
    fontFamily: "Mulish, sans-serif",
  },

  "& h1": {
    fontSize: "1.75rem",
    fontFamily: "Mulish, sans-serif",
    borderBottom: `2px solid ${theme.palette.primary.light}`,
    paddingBottom: "8px",
    marginBottom: "12px",
    width: "100%",
  },
  "& h2": {
    fontSize: "1.5rem",
    fontFamily: "Mulish, sans-serif",
    borderBottom: `1px solid ${theme.palette.primary.light}`,
    paddingBottom: "6px",
    marginBottom: "10px",
    width: "100%",
  },
  "& h3": {
    fontSize: "1.25rem",
    fontFamily: "Mulish, sans-serif",
    borderBottom: "1px solid #ddd",
    paddingBottom: "4px",
    marginBottom: "8px",
    width: "100%",
  },
  "& h4": { fontSize: "1.125rem", fontFamily: "Mulish, sans-serif" },
  "& h5": { fontSize: "1rem", fontFamily: "Mulish, sans-serif" },
  "& h6": { fontSize: "0.875rem", fontFamily: "Mulish, sans-serif" },

  // Blockquote styles
  "& blockquote": {
    borderLeft: `4px solid ${theme.palette.primary.light}`,
    marginLeft: 0,
    paddingLeft: "16px",
    fontStyle: "italic",
    fontFamily: "Mulish, sans-serif",
  },

  // Horizontal rule
  "& hr": {
    border: "none",
    borderTop: "1px solid #ddd",
    margin: "16px 0",
  },

  // Link styles
  "& a": {
    color: theme.palette.primary.light,
    textDecoration: "underline",
    fontFamily: "Mulish, sans-serif",
  },

  // Ensure all elements inherit Mulish font
  "& *": {
    fontFamily: "Mulish, sans-serif !important",
  },
});

// Function to process text and wrap citation patterns in spans
const processCitations = (text) => {
  if (typeof text !== "string") return text;

  // Replace patterns like [1], [2], [3], etc. with styled spans
  // This regex matches [number] followed by punctuation, whitespace, or end of string
  return text.replace(/\[(\d+)\]/g, (match, number) => {
    return `<span class="citation-bubble">${number}</span>`;
  });
};

// Function to process React children and handle citation patterns
const processChildrenForCitations = (children) => {
  if (typeof children === "string") {
    const processedText = processCitations(children);
    if (processedText !== children) {
      return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
    }
    return children;
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === "string") {
        const processedText = processCitations(child);
        if (processedText !== child) {
          return <span key={index} dangerouslySetInnerHTML={{ __html: processedText }} />;
        }
        return child;
      }
      return child;
    });
  }

  return children;
};

// Shared markdown components for ReactMarkdown
export const getMarkdownComponents = (theme) => ({
  // Add text component to handle raw text nodes
  text: ({ children }) => {
    if (typeof children === "string") {
      const processedText = processCitations(children);
      if (processedText !== children) {
        return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
      }
    }
    return children;
  },

  code: ({ inline, children, ...props }) => {
    if (inline) {
      return (
        <code className="inline-code" {...props}>
          {children}
        </code>
      );
    }

    const content = typeof children === "string" ? String(children).replace(/\n$/, "") : children;

    return (
      <pre className="code-block">
        <code {...props}>{content}</code>
      </pre>
    );
  },

  p: ({ children, ...props }) => (
    <p
      style={{
        whiteSpace: "pre-wrap",
        fontFamily: "Mulish, sans-serif",
        fontSize: "16px",
        lineHeight: 1.5,
        margin: "2px 0",
      }}
      {...props}
    >
      {processChildrenForCitations(children)}
    </p>
  ),

  // Custom link component to open in new window
  a: ({ href, children, ...props }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: theme.palette.primary.light,
        textDecoration: "underline",
        fontFamily: "Mulish, sans-serif",
      }}
      {...props}
    >
      {processChildrenForCitations(children)}
    </a>
  ),

  // Custom list components for better spacing
  ul: ({ children, ...props }) => (
    <ul
      style={{
        margin: "0 0 2px 0",
        paddingLeft: "20px",
        listStyleType: "disc",
        fontFamily: "Mulish, sans-serif",
        fontSize: "16px",
      }}
      {...props}
    >
      {processChildrenForCitations(children)}
    </ul>
  ),

  ol: ({ children, ...props }) => (
    <ol
      style={{
        margin: "0 0 2px 0",
        paddingLeft: "20px",
        listStyleType: "decimal",
        fontFamily: "Mulish, sans-serif",
        fontSize: "16px",
      }}
      {...props}
    >
      {processChildrenForCitations(children)}
    </ol>
  ),

  li: ({ children, ...props }) => (
    <li
      style={{
        margin: "0",
        lineHeight: 1.5,
        fontFamily: "Mulish, sans-serif",
        fontSize: "16px",
      }}
      {...props}
    >
      {processChildrenForCitations(children)}
    </li>
  ),
});
